/**
 * Modal « Progression Association vs EDT »
 *
 * Objectif : suivre au jour le jour l'écart entre les étudiants attendus
 * (déterminés par les créneaux EDT × effectifs) et les copies déjà
 * associées, pour repérer les matières en retard et prévenir les pertes.
 *
 * Structure :
 *   - Sélecteur de session EDT + type d'examen (NORMAL / RATTRAPAGE)
 *   - Bloc résumé (nb créneaux, attendus, associés, écart, taux %)
 *   - Onglet « Par matière » : tri par écart décroissant (priorité action)
 *   - Onglet « Par jour » : progression par date (barre + %)
 *   - Onglet « Détail créneaux » : liste complète
 */
import React, { useState, useMemo, useEffect } from 'react';
import { X, RefreshCw, Calendar, BookOpen, AlertTriangle, CheckCircle2, TrendingUp, Filter, Search, AlertOctagon, Printer, BarChart3 } from 'lucide-react';
import { useGetSessionsQuery } from '../../services/emploisTempsApi';
import { useGetProgressionEDTQuery, useGetCopiesHorsEDTQuery } from '../../services/copiesApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isDark?: boolean; // conservé pour compat, mais on préfère les classes dark: tailwind
  defaultTypeExamen?: 'NORMAL' | 'RATTRAPAGE';
}

const ProgressionEDTModal: React.FC<Props> = ({ isOpen, onClose, defaultTypeExamen = 'NORMAL' }) => {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [typeExamen, setTypeExamen] = useState<'NORMAL' | 'RATTRAPAGE'>(defaultTypeExamen);
  const [tab, setTab] = useState<'matiere' | 'jour' | 'stats' | 'creneau' | 'hors_edt'>('matiere');
  // Filtres transversaux — appliqués aux 3 onglets.
  const [matiereSearch, setMatiereSearch] = useState('');
  const [filtreDate, setFiltreDate] = useState<string>('');       // '' = toutes
  const [filtreHeure, setFiltreHeure] = useState<string>('');     // '' = toutes
  const [filtreEcartMin, setFiltreEcartMin] = useState<string>(''); // '' = pas de min
  const [filtreTauxMax, setFiltreTauxMax] = useState<string>(''); // '' = pas de max
  const [showFiltres, setShowFiltres] = useState<boolean>(true);

  const anneeActive = typeof window !== 'undefined'
    ? localStorage.getItem('annee_academique') || undefined
    : undefined;

  const { data: sessions = [] } = useGetSessionsQuery(
    { annee_academique: anneeActive || '' },
    { skip: !isOpen },
  );

  // Auto-sélection : la session la plus récente correspondant au type_examen
  useEffect(() => {
    if (!isOpen || sessionId != null) return;
    if (!sessions.length) return;
    const cible = typeExamen === 'RATTRAPAGE' ? 'rattrapage' : 'ordinaire';
    const match = sessions.find((s: any) => s.type_session === cible) || sessions[0];
    if (match) setSessionId(match.id);
  }, [isOpen, sessions, sessionId, typeExamen]);

  const { data, isFetching, refetch } = useGetProgressionEDTQuery(
    { session_examen_id: sessionId as number, type_examen: typeExamen },
    { skip: !isOpen || !sessionId },
  );

  // Diagnostic « copies hors EDT » — chargé uniquement quand cet onglet
  // est ouvert (évite un fetch coûteux à l'ouverture du modal).
  const { data: horsEDT, isFetching: fetchingHors, refetch: refetchHors } = useGetCopiesHorsEDTQuery(
    { session_examen_id: sessionId as number, type_examen: typeExamen },
    { skip: !isOpen || !sessionId || tab !== 'hors_edt' },
  );

  // Prédicats communs — utilisés par les 3 onglets pour un comportement
  // homogène (les filtres s'appliquent partout).
  const q = matiereSearch.trim().toLowerCase();
  const ecartMin = Number(filtreEcartMin) || 0;
  const tauxMax = filtreTauxMax === '' ? null : Number(filtreTauxMax);
  const heureFiltreNorm = filtreHeure.trim();

  const matchTexteMatiere = (matiere: string, niveau: string, groupe: string) =>
    !q || matiere.toLowerCase().includes(q) || niveau.toLowerCase().includes(q) || groupe.toLowerCase().includes(q);

  // Détails (créneaux) filtrés — base pour recalculer par_jour / par_matiere
  // quand un filtre est actif, afin que les 3 vues restent cohérentes.
  const detailsFiltres = useMemo(() => {
    if (!data?.details) return [];
    return data.details.filter(d => {
      if (filtreDate && d.date !== filtreDate) return false;
      if (heureFiltreNorm && d.heure !== heureFiltreNorm) return false;
      if (ecartMin > 0 && d.ecart < ecartMin) return false;
      if (tauxMax !== null && d.taux_pct > tauxMax) return false;
      if (!matchTexteMatiere(d.matiere, d.niveau, d.groupe)) return false;
      return true;
    });
  }, [data, filtreDate, heureFiltreNorm, ecartMin, tauxMax, q]);

  // Recompose « par matière » à partir des créneaux filtrés (pour rester
  // cohérent avec les filtres date/heure/etc.). Chaque ligne = (matière, niveau, groupe).
  const parMatiereFiltrees = useMemo(() => {
    if (!data) return [];
    // Si aucun filtre créneau actif → on garde l'agrégation initiale (perf)
    const aucunFiltreCreneau = !filtreDate && !heureFiltreNorm && ecartMin === 0 && tauxMax === null;
    if (aucunFiltreCreneau) {
      return (data.par_matiere || []).filter(m => matchTexteMatiere(m.matiere, m.niveau, m.groupe));
    }
    // Sinon → recompose depuis detailsFiltres
    const map = new Map<string, { matiere: string; niveau: string; groupe: string; attendu: number; associe: number; ecart: number; dates: string[] }>();
    for (const d of detailsFiltres) {
      const key = `${d.matiere}||${d.niveau}||${d.groupe}`;
      const entry = map.get(key) || { matiere: d.matiere, niveau: d.niveau, groupe: d.groupe, attendu: 0, associe: 0, ecart: 0, dates: [] };
      entry.attendu += d.effectif_attendu;
      entry.associe += d.copies_associees;
      entry.ecart += d.ecart;
      if (!entry.dates.includes(d.date)) entry.dates.push(d.date);
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) => (b.ecart - a.ecart) || a.matiere.localeCompare(b.matiere));
  }, [data, detailsFiltres, filtreDate, heureFiltreNorm, ecartMin, tauxMax, q]);

  // Recompose « par jour » à partir des créneaux filtrés
  const parJourFiltres = useMemo(() => {
    if (!data) return [];
    const aucunFiltre = !q && !filtreDate && !heureFiltreNorm && ecartMin === 0 && tauxMax === null;
    if (aucunFiltre) return data.par_jour || [];
    const map = new Map<string, { date: string; jour_semaine: string; nb_creneaux: number; attendu: number; associe: number; ecart: number }>();
    for (const d of detailsFiltres) {
      const entry = map.get(d.date) || { date: d.date, jour_semaine: d.jour_semaine, nb_creneaux: 0, attendu: 0, associe: 0, ecart: 0 };
      entry.nb_creneaux += 1;
      entry.attendu += d.effectif_attendu;
      entry.associe += d.copies_associees;
      entry.ecart += d.ecart;
      map.set(d.date, entry);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, detailsFiltres, q, filtreDate, heureFiltreNorm, ecartMin, tauxMax]);

  // Listes distinctes pour les selects (dates, heures)
  const datesUniques = useMemo(() => {
    if (!data?.details) return [];
    const set = new Set<string>();
    for (const d of data.details) set.add(d.date);
    return Array.from(set).sort();
  }, [data]);
  const heuresUniques = useMemo(() => {
    if (!data?.details) return [];
    const set = new Set<string>();
    for (const d of data.details) set.add(d.heure);
    return Array.from(set).sort();
  }, [data]);

  const resetFiltres = () => {
    setMatiereSearch(''); setFiltreDate(''); setFiltreHeure('');
    setFiltreEcartMin(''); setFiltreTauxMax('');
  };
  const nbFiltresActifs = [
    q, filtreDate, heureFiltreNorm,
    ecartMin > 0 ? '1' : '', tauxMax !== null ? '1' : '',
  ].filter(Boolean).length;

  if (!isOpen) return null;

  const badgeCls = (pct: number) => {
    if (pct >= 95) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700';
    if (pct >= 70) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-700';
    if (pct >= 40) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-300 dark:border-orange-700';
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-300 dark:border-red-700';
  };

  const iconEtat = (pct: number) => {
    if (pct >= 95) return <CheckCircle2 size={12} className="text-emerald-500" />;
    if (pct >= 70) return <TrendingUp size={12} className="text-amber-500" />;
    return <AlertTriangle size={12} className="text-red-500" />;
  };

  const resume = data?.resume;
  const s = data?.session;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-6xl max-h-[92vh] rounded-xl shadow-2xl flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={20} className="text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Progression Association vs EDT</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300">
            <X size={18} />
          </button>
        </div>

        {/* Contrôles */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 flex flex-wrap items-center gap-2 text-sm">
          <label className="text-xs text-gray-500 dark:text-gray-400">Session EDT :</label>
          <select
            value={sessionId ?? ''}
            onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : null)}
            className="px-2 py-1 rounded border text-xs bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">— Sélectionner —</option>
            {sessions.map((sess: any) => (
              <option key={sess.id} value={sess.id}>
                {sess.nom} · S{sess.semestre} · {sess.type_session} · {sess.type_formation || 'FI'}
              </option>
            ))}
          </select>

          <label className="text-xs text-gray-500 dark:text-gray-400 ml-2">Type examen :</label>
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setTypeExamen('NORMAL')}
              className={`px-2 py-1 text-[11px] font-medium ${
                typeExamen === 'NORMAL' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >Ordinaire</button>
            <button
              onClick={() => setTypeExamen('RATTRAPAGE')}
              className={`px-2 py-1 text-[11px] font-medium border-l border-gray-300 dark:border-gray-600 ${
                typeExamen === 'RATTRAPAGE' ? 'bg-orange-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >Rattrapage</button>
          </div>

          <button
            onClick={() => refetch()}
            disabled={isFetching || !sessionId}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            title="Rafraîchir les chiffres"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Rafraîchir
          </button>
          <button
            onClick={() => imprimerStats(data, typeExamen)}
            disabled={!data}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-blue-400 text-blue-600 dark:text-blue-300 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
            title="Imprimer un rapport A4 avec les stats et compteurs"
          >
            <Printer size={12} /> Imprimer
          </button>
        </div>

        {/* Résumé */}
        {resume && s && (
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatBox label="Créneaux EDT" value={resume.nb_creneaux} icon={<Calendar size={14} />} />
            <StatBox label="Étudiants attendus" value={resume.total_attendu} accent="blue" />
            <StatBox label="Copies associées" value={resume.total_associe} accent="emerald" />
            <StatBox label="Écart restant" value={resume.total_ecart} accent="red" />
            <StatBox label="Taux couverture" value={`${resume.taux_couverture_pct}%`} accent="amber" />
          </div>
        )}

        {/* Barre de filtres transversale */}
        <div className="px-5 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowFiltres(v => !v)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${
                nbFiltresActifs > 0
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              title={showFiltres ? 'Masquer les filtres' : 'Afficher les filtres'}
            >
              <Filter size={12} />
              Filtres
              {nbFiltresActifs > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold">{nbFiltresActifs}</span>
              )}
            </button>
            {nbFiltresActifs > 0 && (
              <button
                onClick={resetFiltres}
                className="text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
              >
                Réinitialiser
              </button>
            )}
            <div className="ml-auto text-[11px] text-gray-500 dark:text-gray-400">
              {detailsFiltres.length}/{data?.details?.length || 0} créneau(x) affiché(s)
            </div>
          </div>
          {showFiltres && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {/* Recherche matière */}
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                <input
                  type="text"
                  value={matiereSearch}
                  onChange={(e) => setMatiereSearch(e.target.value)}
                  placeholder="Rechercher matière / niveau / groupe…"
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              {/* Filtre date */}
              <select
                value={filtreDate}
                onChange={(e) => setFiltreDate(e.target.value)}
                className="px-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                title="Filtrer par jour"
              >
                <option value="">Toutes les dates</option>
                {datesUniques.map(d => (
                  <option key={d} value={d}>
                    {new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })}
                  </option>
                ))}
              </select>
              {/* Filtre heure */}
              <select
                value={filtreHeure}
                onChange={(e) => setFiltreHeure(e.target.value)}
                className="px-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                title="Filtrer par créneau horaire"
              >
                <option value="">Toutes les heures</option>
                {heuresUniques.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              {/* Filtre écart minimum */}
              <input
                type="number"
                min={0}
                value={filtreEcartMin}
                onChange={(e) => setFiltreEcartMin(e.target.value)}
                placeholder="Écart ≥ (ex : 5)"
                className="px-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                title="Ne montrer que les créneaux avec un écart ≥ N"
              />
              {/* Filtre taux max */}
              <input
                type="number"
                min={0}
                max={100}
                value={filtreTauxMax}
                onChange={(e) => setFiltreTauxMax(e.target.value)}
                placeholder="Taux ≤ % (ex : 80)"
                className="px-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                title="Ne montrer que les créneaux avec un taux de couverture ≤ N %"
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="px-5 pt-2 border-b border-gray-200 dark:border-gray-800 flex gap-1">
          {[
            { k: 'matiere', l: 'Par matière', i: <BookOpen size={13} /> },
            { k: 'jour', l: 'Par jour', i: <Calendar size={13} /> },
            { k: 'stats', l: 'Stats', i: <BarChart3 size={13} /> },
            { k: 'creneau', l: 'Détail créneaux', i: <TrendingUp size={13} /> },
            { k: 'hors_edt', l: '⚠ Hors EDT', i: <AlertOctagon size={13} /> },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k as any)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md flex items-center gap-1 border-b-2 ${
                tab === t.k
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >{t.i} {t.l}</button>
          ))}
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-auto p-5 text-sm text-gray-900 dark:text-gray-100">
          {!sessionId && (
            <div className="text-center py-10 text-gray-500">Sélectionnez une session EDT pour afficher la progression.</div>
          )}
          {sessionId && isFetching && (
            <div className="text-center py-10 text-gray-500">Chargement…</div>
          )}
          {sessionId && !isFetching && data && (
            <>
              {tab === 'matiere' && (
                <div className="space-y-2">
                  {/* La recherche + filtres sont dans la barre en haut du modal
                      (transversale aux 3 onglets). */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                          <th className="text-left py-2 px-2 text-[11px] uppercase font-semibold">Matière</th>
                          <th className="text-left py-2 px-2 text-[11px] uppercase font-semibold">Niv.</th>
                          <th className="text-left py-2 px-2 text-[11px] uppercase font-semibold">Groupe</th>
                          <th className="text-right py-2 px-2 text-[11px] uppercase font-semibold">Attendus</th>
                          <th className="text-right py-2 px-2 text-[11px] uppercase font-semibold">Associés</th>
                          <th className="text-right py-2 px-2 text-[11px] uppercase font-semibold">Écart</th>
                          <th className="text-center py-2 px-2 text-[11px] uppercase font-semibold">Taux</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parMatiereFiltrees.length === 0 && (
                          <tr><td colSpan={7} className="text-center py-6 text-gray-500">Aucune matière trouvée.</td></tr>
                        )}
                        {parMatiereFiltrees.map((m, i) => {
                          const pct = m.attendu > 0 ? Math.round((m.associe / m.attendu) * 100) : 0;
                          return (
                            <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                              <td className="py-1.5 px-2 font-medium">{m.matiere}</td>
                              <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400">{m.niveau}</td>
                              <td className="py-1.5 px-2 text-xs text-gray-500 dark:text-gray-400">{m.groupe}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums">{m.attendu}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{m.associe}</td>
                              <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${m.ecart > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{m.ecart}</td>
                              <td className="py-1.5 px-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badgeCls(pct)}`}>
                                  {iconEtat(pct)} {pct}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tab === 'jour' && (
                <div className="space-y-2">
                  {parJourFiltres.length === 0 && <div className="text-center py-6 text-gray-500">Aucun jour ne correspond aux filtres.</div>}
                  {parJourFiltres.map((j) => {
                    const pct = j.attendu > 0 ? Math.round((j.associe / j.attendu) * 100) : 0;
                    const dateFr = new Date(j.date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });
                    return (
                      <div key={j.date} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-blue-500" />
                            <span className="font-semibold capitalize">{dateFr}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">· {j.nb_creneaux} créneau(x)</span>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badgeCls(pct)}`}>
                            {iconEtat(pct)} {pct}%
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                          <div>Attendus : <span className="font-semibold text-blue-600 dark:text-blue-400">{j.attendu}</span></div>
                          <div>Associés : <span className="font-semibold text-emerald-600 dark:text-emerald-400">{j.associe}</span></div>
                          <div>Écart : <span className={`font-semibold ${j.ecart > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{j.ecart}</span></div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Participation :</span>{' '}
                            <span className={`font-bold text-base tabular-nums ${
                              pct >= 95 ? 'text-emerald-600 dark:text-emerald-400'
                              : pct >= 70 ? 'text-amber-600 dark:text-amber-400'
                              : pct >= 40 ? 'text-orange-600 dark:text-orange-400'
                              : 'text-red-600 dark:text-red-400'
                            }`}>{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                          <div
                            className={`h-full transition-all ${pct >= 95 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'stats' && (
                <div className="space-y-4">
                  <StatsBlock
                    title="Par niveau"
                    rows={(data.par_niveau || []).map(r => ({
                      key: r.niveau, label: r.niveau, sub: `${r.nb_creneaux} créneau(x)`,
                      attendu: r.attendu, associe: r.associe, ecart: r.ecart, taux: r.taux_pct,
                    }))}
                    badgeCls={badgeCls}
                    emptyText="Aucune donnée par niveau."
                  />
                  <StatsBlock
                    title="Par pôle"
                    rows={(data.par_pole || []).map(r => ({
                      key: r.pole, label: r.pole || '—', sub: `${r.nb_creneaux} créneau(x)`,
                      attendu: r.attendu, associe: r.associe, ecart: r.ecart, taux: r.taux_pct,
                    }))}
                    badgeCls={badgeCls}
                    emptyText="Aucune donnée par pôle."
                  />
                  <StatsBlock
                    title="Par site (via brassage)"
                    rows={(data.par_site || []).map(r => ({
                      key: r.site, label: r.site || '—', sub: `${r.nb_etudiants} étudiant(s) placé(s)`,
                      attendu: r.attendu, associe: r.associe, ecart: r.ecart, taux: r.taux_pct,
                    }))}
                    badgeCls={badgeCls}
                    emptyText="Aucun brassage lié à cette session (pas de répartition par site disponible)."
                  />
                </div>
              )}

              {tab === 'hors_edt' && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 p-3 text-xs">
                    <div className="font-semibold text-red-900 dark:text-red-200 mb-1 flex items-center gap-2">
                      <AlertOctagon size={14} /> Diagnostic — copies associées HORS EDT de cette session
                    </div>
                    <p className="text-red-800 dark:text-red-300">
                      Ces copies ({typeExamen}) sont rattachées à une matière qui n'est PAS
                      dans les créneaux EDT de la session sélectionnée. Elles proviennent
                      probablement d'une erreur d'association — vérifiez et dissociez pour
                      les réassocier à la bonne matière.
                    </p>
                    <button
                      onClick={() => refetchHors()}
                      className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 text-[11px]"
                    >
                      <RefreshCw size={11} /> Relancer l'analyse
                    </button>
                  </div>
                  {fetchingHors && <div className="text-center py-6 text-gray-500">Analyse en cours…</div>}
                  {!fetchingHors && horsEDT && horsEDT.nb_matieres_hors_edt === 0 && (
                    <div className="text-center py-8 text-emerald-600 dark:text-emerald-400 text-sm">
                      ✓ Aucune anomalie détectée — toutes les copies {typeExamen} de la période
                      sont rattachées à une matière prévue par l'EDT.
                    </div>
                  )}
                  {!fetchingHors && horsEDT && horsEDT.nb_matieres_hors_edt > 0 && (
                    <>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <strong className="text-red-600 dark:text-red-400">{horsEDT.nb_copies_hors_edt}</strong> copie(s)
                        sur <strong>{horsEDT.nb_matieres_hors_edt}</strong> matière(s) sont hors EDT.
                      </div>
                      <div className="space-y-2">
                        {horsEDT.matieres_hors_edt.map((m) => (
                          <div key={m.matiere_id} className="rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 p-3">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-sm">
                                  {m.matiere_code} · {m.matiere_nom}
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                  Niveau {m.niveau} · Semestre {m.semestre}
                                </div>
                              </div>
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-bold shrink-0">
                                {m.nb_copies} copie(s)
                              </span>
                            </div>
                            {m.exemples_codes.length > 0 && (
                              <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                                Exemples de codes : <span className="font-mono">{m.exemples_codes.join(', ')}</span>
                              </div>
                            )}
                            {m.suggestions.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                                  ✓ Matière(s) EDT compatible(s) proposée(s) :
                                </div>
                                <ul className="space-y-0.5">
                                  {m.suggestions.map((s) => (
                                    <li key={s.id} className="text-[11px] text-gray-700 dark:text-gray-300">
                                      → <strong>{s.code_matiere}</strong> · {s.nom_matiere}
                                      <span className="text-gray-400 ml-1">
                                        (S{s.semestre}, {s.niveau})
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                                <div className="mt-1.5 text-[10px] text-gray-500 italic">
                                  Utilisez « Dissocier en lot » pour libérer ces copies et les réassocier à la bonne matière.
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === 'creneau' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                        <th className="text-left py-1.5 px-2">Date</th>
                        <th className="text-left py-1.5 px-2">Heure</th>
                        <th className="text-left py-1.5 px-2">Matière</th>
                        <th className="text-left py-1.5 px-2">Niv.</th>
                        <th className="text-left py-1.5 px-2">Groupe</th>
                        <th className="text-right py-1.5 px-2">Att.</th>
                        <th className="text-right py-1.5 px-2">Ass.</th>
                        <th className="text-right py-1.5 px-2">Écart</th>
                        <th className="text-center py-1.5 px-2">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsFiltres.length === 0 && (
                        <tr><td colSpan={9} className="text-center py-6 text-gray-500">Aucun créneau ne correspond aux filtres.</td></tr>
                      )}
                      {detailsFiltres.map((d) => (
                        <tr key={d.creneau_id} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-1 px-2 tabular-nums">{new Date(d.date).toLocaleDateString('fr-FR')}</td>
                          <td className="py-1 px-2 tabular-nums text-gray-500 dark:text-gray-400">{d.heure}</td>
                          <td className="py-1 px-2 font-medium">{d.matiere}</td>
                          <td className="py-1 px-2 text-gray-500 dark:text-gray-400">{d.niveau}</td>
                          <td className="py-1 px-2 text-gray-500 dark:text-gray-400 max-w-[180px] truncate" title={d.groupe}>{d.groupe}</td>
                          <td className="py-1 px-2 text-right tabular-nums">{d.effectif_attendu}</td>
                          <td className="py-1 px-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{d.copies_associees}</td>
                          <td className={`py-1 px-2 text-right tabular-nums font-semibold ${d.ecart > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{d.ecart}</td>
                          <td className="py-1 px-2 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${badgeCls(d.taux_pct)}`}>{d.taux_pct}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StatBox: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode; accent?: 'blue' | 'emerald' | 'red' | 'amber' }> = ({ label, value, icon, accent }) => {
  const color = accent === 'blue' ? 'text-blue-600 dark:text-blue-400'
    : accent === 'emerald' ? 'text-emerald-600 dark:text-emerald-400'
    : accent === 'red' ? 'text-red-600 dark:text-red-400'
    : accent === 'amber' ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-900 dark:text-gray-100';
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 text-center">
      <div className={`text-lg font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
        {icon}{label}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StatsBlock — bloc réutilisable pour Par niveau / Par pôle / Par site
// ─────────────────────────────────────────────────────────────────────────────
interface StatsRow {
  key: string; label: string; sub?: string;
  attendu: number; associe: number; ecart: number; taux: number;
}
const StatsBlock: React.FC<{
  title: string; rows: StatsRow[]; emptyText: string;
  badgeCls: (pct: number) => string;
}> = ({ title, rows, emptyText, badgeCls }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/70 text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
      {title}
    </div>
    {rows.length === 0 ? (
      <div className="p-4 text-xs text-gray-500 italic text-center">{emptyText}</div>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
            <th className="text-left py-1.5 px-3 text-[11px] uppercase font-semibold">Libellé</th>
            <th className="text-right py-1.5 px-2 text-[11px] uppercase font-semibold">Attendus</th>
            <th className="text-right py-1.5 px-2 text-[11px] uppercase font-semibold">Associés</th>
            <th className="text-right py-1.5 px-2 text-[11px] uppercase font-semibold">Écart</th>
            <th className="text-center py-1.5 px-2 text-[11px] uppercase font-semibold">Taux</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-1.5 px-3">
                <div className="font-medium">{r.label}</div>
                {r.sub && <div className="text-[10px] text-gray-500 dark:text-gray-400">{r.sub}</div>}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums">{r.attendu}</td>
              <td className="py-1.5 px-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{r.associe}</td>
              <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${r.ecart > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{r.ecart}</td>
              <td className="py-1.5 px-2 text-center">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badgeCls(r.taux)}`}>{r.taux}%</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// imprimerStats — génère un rapport A4 imprimable (nouvelle fenêtre)
// ─────────────────────────────────────────────────────────────────────────────
function imprimerStats(data: any, typeExamen: string) {
  if (!data) return;
  const s = data.session || {};
  const r = data.resume || {};
  const sess_label = ({ normal: 'Session Ordinaire', rattrapage: 'Session de Rattrapage', mixte: 'Session Mixte' } as any)[typeExamen === 'RATTRAPAGE' ? 'rattrapage' : 'normal'] || typeExamen;

  const colorFor = (pct: number) =>
    pct >= 95 ? '#059669' : pct >= 70 ? '#d97706' : pct >= 40 ? '#ea580c' : '#dc2626';

  const rowsHtml = (arr: any[], colKey: string, subFmt?: (r: any) => string) => arr.map((row: any) => `
    <tr>
      <td>${row[colKey] || '—'}${subFmt ? `<div style="font-size:8px;color:#666">${subFmt(row)}</div>` : ''}</td>
      <td class="right">${row.attendu}</td>
      <td class="right green">${row.associe}</td>
      <td class="right ${row.ecart > 0 ? 'red' : 'gray'}">${row.ecart}</td>
      <td class="center"><span class="badge" style="color:${colorFor(row.taux_pct)};border-color:${colorFor(row.taux_pct)}">${row.taux_pct}%</span></td>
    </tr>`).join('');

  const parJourRowsHtml = (data.par_jour || []).map((j: any) => `
    <tr>
      <td><b>${j.jour_semaine || ''}</b> ${new Date(j.date).toLocaleDateString('fr-FR')}<div style="font-size:8px;color:#666">${j.nb_creneaux} créneau(x)</div></td>
      <td class="right">${j.attendu}</td>
      <td class="right green">${j.associe}</td>
      <td class="right ${j.ecart > 0 ? 'red' : 'gray'}">${j.ecart}</td>
      <td class="center"><span class="badge" style="color:${colorFor(j.taux_pct)};border-color:${colorFor(j.taux_pct)}">${j.taux_pct}%</span></td>
    </tr>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rapport Progression EDT</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; font-family: 'Helvetica', Arial, sans-serif; }
  body { color: #1a1a1a; font-size: 10pt; }
  h1 { color: #1e3a5f; font-size: 14pt; margin: 0 0 4px 0; }
  h2 { color: #1e3a5f; font-size: 11pt; margin: 12px 0 4px 0; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
  .subtitle { color: #666; font-size: 9pt; margin-bottom: 8px; }
  .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; margin-bottom: 10px; }
  .kpi { border: 1px solid #ddd; border-radius: 4px; padding: 6px; text-align: center; }
  .kpi .v { font-size: 14pt; font-weight: bold; color: #1e3a5f; }
  .kpi .l { font-size: 8pt; color: #666; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9pt; }
  th { background: #f0f0f0; color: #333; text-align: left; padding: 4px 6px; border-bottom: 1px solid #999; font-size: 8pt; text-transform: uppercase; }
  td { padding: 3px 6px; border-bottom: 1px solid #eee; }
  .right { text-align: right; font-variant-numeric: tabular-nums; }
  .center { text-align: center; }
  .green { color: #059669; font-weight: bold; }
  .red { color: #dc2626; font-weight: bold; }
  .gray { color: #999; }
  .badge { display: inline-block; padding: 1px 6px; border: 1px solid; border-radius: 8px; font-size: 8pt; font-weight: bold; }
  .foot { margin-top: 15px; font-size: 8pt; color: #666; text-align: right; font-style: italic; }
  @media print { .noprint { display: none; } }
</style></head><body>
  <h1>Rapport Progression Association vs EDT</h1>
  <div class="subtitle">
    <b>${s.nom || ''}</b> · ${sess_label} · Semestre ${s.semestre ?? ''} · ${s.type_formation || 'FI'}
    · A.A. ${s.annee_academique || ''}<br/>
    Période : ${s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '—'} → ${s.date_fin ? new Date(s.date_fin).toLocaleDateString('fr-FR') : '—'}
  </div>

  <div class="kpis">
    <div class="kpi"><div class="v">${r.nb_creneaux ?? 0}</div><div class="l">Créneaux EDT</div></div>
    <div class="kpi"><div class="v" style="color:#2563eb">${r.total_attendu ?? 0}</div><div class="l">Étudiants attendus</div></div>
    <div class="kpi"><div class="v" style="color:#059669">${r.total_associe ?? 0}</div><div class="l">Copies associées</div></div>
    <div class="kpi"><div class="v" style="color:#dc2626">${r.total_ecart ?? 0}</div><div class="l">Écart</div></div>
    <div class="kpi"><div class="v" style="color:${colorFor(r.taux_couverture_pct || 0)}">${r.taux_couverture_pct ?? 0}%</div><div class="l">Couverture</div></div>
  </div>

  <h2>Participation journalière</h2>
  <table>
    <thead><tr><th>Jour</th><th class="right">Attendus</th><th class="right">Associés</th><th class="right">Écart</th><th class="center">Taux</th></tr></thead>
    <tbody>${parJourRowsHtml || '<tr><td colspan="5" class="center gray">Aucun jour</td></tr>'}</tbody>
  </table>

  <h2>Répartition par niveau</h2>
  <table>
    <thead><tr><th>Niveau</th><th class="right">Attendus</th><th class="right">Associés</th><th class="right">Écart</th><th class="center">Taux</th></tr></thead>
    <tbody>${rowsHtml(data.par_niveau || [], 'niveau', (r) => `${r.nb_creneaux} créneau(x)`) || '<tr><td colspan="5" class="center gray">—</td></tr>'}</tbody>
  </table>

  <h2>Répartition par pôle</h2>
  <table>
    <thead><tr><th>Pôle</th><th class="right">Attendus</th><th class="right">Associés</th><th class="right">Écart</th><th class="center">Taux</th></tr></thead>
    <tbody>${rowsHtml(data.par_pole || [], 'pole', (r) => `${r.nb_creneaux} créneau(x)`) || '<tr><td colspan="5" class="center gray">—</td></tr>'}</tbody>
  </table>

  <h2>Répartition par site (via brassage)</h2>
  <table>
    <thead><tr><th>Site</th><th class="right">Attendus</th><th class="right">Associés</th><th class="right">Écart</th><th class="center">Taux</th></tr></thead>
    <tbody>${rowsHtml(data.par_site || [], 'site', (r) => `${r.nb_etudiants} étud. placé(s)`) || '<tr><td colspan="5" class="center gray">Aucun brassage lié</td></tr>'}</tbody>
  </table>

  <div class="foot">Généré le ${new Date().toLocaleString('fr-FR')} — Institut International 2i</div>
  <div class="noprint" style="margin-top:10px;text-align:center">
    <button onclick="window.print()" style="padding:8px 20px;background:#2563eb;color:#fff;border:0;border-radius:4px;cursor:pointer;font-size:11pt">Imprimer</button>
    <button onclick="window.close()" style="margin-left:8px;padding:8px 20px;background:#666;color:#fff;border:0;border-radius:4px;cursor:pointer;font-size:11pt">Fermer</button>
  </div>
  <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
</body></html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  if (win) { win.document.write(html); win.document.close(); }
}

export default ProgressionEDTModal;
